import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Navbar from "../Navbar/Main";
import Footer from "../Footer/Main";
import LRsidebar from "./lrsidebar";
import { Pagination } from "../DataTable";
import ShareToolsBar from "../Publications/ShareToolsBar";
import "../Publications/PublicationsMain.css";
import "./banking.css";

export const Banking = () => {
  const [clickedKey, setClickedKey] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState("laws");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    axios
      .get("/banking_laws")
      .then((res) => {
        setDocuments(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load documents.");
        setLoading(false);
      });
  }, []);

  const categories = [
    { key: "laws", label: "Laws", icon: "⚖️" },
    { key: "regulations", label: "Regulations", icon: "🔨" },
    { key: "directives", label: "Directives", icon: "📢" },
    { key: "guidelines", label: "Guidelines", icon: "📖" },
  ];

  const counts = useMemo(
    () =>
      documents.reduce(
        (acc, d) => {
          const type = d.type || "laws";
          acc[type] = (acc[type] || 0) + 1;
          return acc;
        },
        { laws: 0, regulations: 0, directives: 0, guidelines: 0 }
      ),
    [documents]
  );

  const filteredDocs = useMemo(
    () => documents.filter((d) => (d.type || "laws") === selectedCategory),
    [documents, selectedCategory]
  );

  const paginatedDocs = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredDocs.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredDocs, currentPage]);

  if (loading) return <p className="text-center">Loading documents...</p>;
  if (error) return <p className="text-center text-danger">{error}</p>;

  return (
    <>
      <Navbar />

      <nav className="publications-bnr-breadcrumb-bar" aria-label="Breadcrumb">
        <div className="container">
          <ol className="publications-bnr-breadcrumb">
            <li>
              <Link to="/">Home</Link>
            </li>
            <li className="publications-bnr-breadcrumb__sep" aria-hidden="true">
              ›
            </li>
            <li>
              <Link to="/lawsandregulations">Laws and Regulations</Link>
            </li>
            <li className="publications-bnr-breadcrumb__sep" aria-hidden="true">
              ›
            </li>
            <li aria-current="page">Banking</li>
          </ol>
        </div>
      </nav>

      <section className="publications-bnr-hero" aria-labelledby="banking-page-title">
        <div className="publications-bnr-hero__bg" aria-hidden="true" />
        <div className="publications-bnr-hero__overlay" aria-hidden="true" />
        <div className="container">
          <div className="publications-bnr-hero__card">
            <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
              <div className="flex-grow-1" style={{ minWidth: "min(100%, 16rem)" }}>
                <h1 id="banking-page-title" className="publications-bnr-hero__title">
                  Banking
                </h1>
                <span className="publications-bnr-hero__title-line" aria-hidden="true" />
                <p className="small publications-bnr-hero__tagline mt-2 mb-0">
                  Laws, regulations and directives governing Rwanda's banking sector
                </p>
              </div>
              <div className="flex-shrink-0">
                <ShareToolsBar pageTitle="Banking - Laws and Regulations" />
              </div>
            </div>
            <div className="publications-bnr-hero__lead">
              <p>
                The National Bank of Rwanda is the Supervisor and Regulator of the banking system in Rwanda.
                The promotion, soundness and stability of banking are attained through implementation of
                international principles and standards as reviewed from time to time by competent bodies such as
                the International Monetary Fund (IMF), Bank for International Settlements (BIS) and Financial
                Stability Institute (FSI).
              </p>
              <p>
                This section contains legal instruments pertaining to the banking system in Rwanda and is designed
                to ensure banks adhere to prudential norms and robust protection of depositors.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="service-details banking-ecb pt-50 pb-100">
        <div className="container">
          <div className="row row-gutter-y-40">
            <div className="col-lg-8">
              <div className="service-details__content">
                <p className="service-details__text">The following are laws and regulations related to Banking</p>

                <div className="faq-tabs mb-4">
                  {categories.map(({ key, label, icon }) => (
                    <button
                      key={key}
                      onClick={() => {
                        setSelectedCategory(key);
                        setCurrentPage(1);
                        setClickedKey(null);
                      }}
                      className={`faq-tab-button position-relative ${selectedCategory === key ? "active" : ""}`}
                    >
                      <span className="me-2">{icon}</span>
                      {label}
                      <span className="faq-count-circle">{counts[key] || 0}</span>
                    </button>
                  ))}
                </div>

                {paginatedDocs.length === 0 ? (
                  <p className="text-center">No documents found.</p>
                ) : (
                  paginatedDocs.map((d, index) => {
                    const itemKey = String(d.id ?? d.file ?? `${d.name ?? "doc"}-${index}`);
                    const isOpen = clickedKey === itemKey;
                    return (
                    <div
                      key={itemKey}
                      className={`accrodion-grp service-details__accrodion mb-3 ${isOpen ? "active" : ""}`}
                      data-grp-name="service-details__accrodion-1"
                    >
                      <div className={`accrodion wow fadeInUp ${isOpen ? "active" : ""}`} data-wow-delay="0ms">
                        <div
                          className="accrodion-title"
                          onClick={() => setClickedKey(isOpen ? null : itemKey)}
                        >
                          <h4>{d.name}</h4>
                          <span className="accrodion-icon"></span>
                        </div>
                        {isOpen && (
                          <div className="accrodion-content">
                            <div className="inner_doc">
                              <p>{d.summary}</p>
                              <Link to={d.file} target="_blank">
                                Download/View <i className="fa fa-file-pdf"></i>
                              </Link>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )})
                )}

                <div className="d-flex justify-content-center mt-4">
                  <Pagination
                    total={filteredDocs.length}
                    itemsPerPage={ITEMS_PER_PAGE}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <LRsidebar />
            </div>
          </div>
        </div>
      </section>

      <Link to="#" data-target="html" className="scroll-to-target scroll-to-top">
        <i className="fa fa-angle-up"></i>
      </Link>

      <Footer />
    </>
  );
};
